import React from 'react';
import { useNavigate } from 'react-router-dom';
import ContactLogo from '../components/shared/ContactLogo';
import HeroDither from '../components/shared/HeroDither';
import Reveal, { REVEAL_CSS } from '../components/shared/Reveal';
import useSeoMeta from '../components/shared/useSeoMeta';
import RelatedPagesLinks from '../components/shared/RelatedPagesLinks';

const IconArrowLeft = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
    <path d="M19 12H5M12 5l-7 7 7 7"/>
  </svg>
);

const IconArrowRight = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const IconEye = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
  </svg>
);

const IconTrend = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 6l-9.5 9.5-5-5L1 18"/><path d="M17 6h6v6"/>
  </svg>
);

const IconCalendar = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/>
  </svg>
);

const IconDownload = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><path d="M7 10l5 5 5-5M12 15V3"/>
  </svg>
);

const METRIC_CARDS = [
  { icon: <IconEye/>, title: 'Visitas en tiempo real', desc: 'Cada vez que alguien abre tu perfil, lo ves aparecer en tu panel al instante, sin recargar nada.' },
  { icon: <IconTrend/>, title: 'Tasa de conversión', desc: 'Cuántas de esas visitas terminan dejando sus datos como lead. El número que de verdad importa.' },
  { icon: <IconCalendar/>, title: 'Comparación mes a mes', desc: 'Gráficos de tendencia que muestran si tu red está creciendo, con el mismo mes del período anterior como referencia.' },
  { icon: <IconDownload/>, title: 'Exporta cuando quieras', desc: 'Baja tu lista completa de leads con un clic, en el formato que tu equipo ya usa.' },
];

const CHART_ITEMS = [
  { title: 'Visitas por día', desc: 'Una línea de tiempo diaria, para ver si un canal o un evento puntual movió la aguja.' },
  { title: 'Leads por fuente', desc: 'Cuántos leads vinieron de un escaneo, de tu perfil público o de una campaña específica.' },
  { title: 'Rendimiento por perfil', desc: 'Si administras más de un perfil, comparas cuál está generando más interacciones.' },
];

const FAQS = [
  { q: '¿Las métricas son en tiempo real?', a: 'Sí, cada visita a tu perfil aparece en tu panel sin que tengas que recargar nada.' },
  { q: '¿Puedo ver qué leads vinieron de un evento específico?', a: 'Sí, si usaste Campañas para ese evento, la atribución queda hecha automáticamente.' },
  { q: '¿Qué incluye el plan gratuito?', a: '4 métricas básicas de tu perfil. Las analíticas avanzadas y de equipo son de los planes Pro y Enterprise.' },
];

const PAGE_TITLE = 'Analítica de Contactos y Leads en Tiempo Real | Contact';
const PAGE_DESCRIPTION = 'Mide visitas, tasa de conversión y tendencia mensual de tu perfil digital. Exporta tus leads con un clic. Analítica de equipo para empresas.';

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: FAQS.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
};

const AnalyticsPage = () => {
  const navigate = useNavigate();
  useSeoMeta({ title: PAGE_TITLE, description: PAGE_DESCRIPTION, path: '/analytics' });

  return (
    <div className="an-root">
      <HeroDither/>
      <header className="an-nav">
        <div className="an-nav-inner">
          <ContactLogo clickable/>
          <div className="an-nav-actions">
            <button onClick={() => navigate('/')} className="an-btn-back">
              <IconArrowLeft/> Volver
            </button>
            <button onClick={() => navigate('/comprar')} className="an-btn-comprar-nav">Comprar</button>
            <button onClick={() => navigate('/login')} className="an-btn-acceder">Acceder</button>
          </div>
        </div>
      </header>

      <section className="an-hero">
        <div className="an-container">
          <p className="an-eyebrow">Analytics</p>
          <h1 className="an-h1">Cada visita, cada clic, medido al instante.</h1>
          <p className="an-lead">
            Compartir tu perfil sin saber qué pasa después es trabajar a ciegas. Contact te muestra
            exactamente cómo se mueve tu red: quién te visita, cuántos se convierten en leads y cómo
            va cambiando mes a mes.
          </p>
        </div>
      </section>

      <section className="an-section">
        <div className="an-container">
          <Reveal>
          <h2 className="an-h2">Un dashboard ejecutivo, no una lista de números sueltos.</h2>
          <div className="an-grid">
            {METRIC_CARDS.map((c) => (
              <div key={c.title} className="an-card">
                <span className="an-card-icon">{c.icon}</span>
                <h3 className="an-card-title">{c.title}</h3>
                <p className="an-card-desc">{c.desc}</p>
              </div>
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      <section className="an-section">
        <div className="an-container">
          <Reveal>
          <h2 className="an-h2 an-h2--tight">Los gráficos que de verdad usas cada semana.</h2>
          <div className="an-chart-grid">
            {CHART_ITEMS.map((c) => (
              <div key={c.title} className="an-chart-item">
                <h4 className="an-chart-title">{c.title}</h4>
                <p className="an-chart-desc">{c.desc}</p>
              </div>
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      <section className="an-section">
        <div className="an-container">
          <Reveal>
          <h2 className="an-h2 an-h2--tight">Analítica de equipo, no solo de un perfil.</h2>
          <div className="an-team-grid">
            <div className="an-team-card">
              <h4 className="an-team-title">Ranking por campaña</h4>
              <p className="an-team-desc">Ve qué miembro de tu equipo está capturando y convirtiendo más en cada evento.</p>
            </div>
            <div className="an-team-card">
              <h4 className="an-team-title">Múltiples perfiles, un panel</h4>
              <p className="an-team-desc">Administra el rendimiento de todos los perfiles de tu empresa desde un solo lugar.</p>
            </div>
          </div>
          </Reveal>
        </div>
      </section>

      <section className="an-section">
        <div className="an-container">
          <Reveal>
          <h2 className="an-h2 an-h2--tight">Empieza con lo básico, sube cuando lo necesites.</h2>
          <div className="an-plan-grid">
            <div className="an-plan-card">
              <h4 className="an-plan-name">Gratuito</h4>
              <p className="an-plan-desc">4 métricas básicas de tu perfil, siempre visibles.</p>
            </div>
            <div className="an-plan-card an-plan-card--highlight">
              <h4 className="an-plan-name">Pro</h4>
              <p className="an-plan-desc">Analíticas avanzadas completas, con tendencia y exportación.</p>
            </div>
            <div className="an-plan-card">
              <h4 className="an-plan-name">Enterprise</h4>
              <p className="an-plan-desc">Todo lo del Pro, más analítica de equipo y ranking por campaña.</p>
            </div>
          </div>
          </Reveal>
        </div>
      </section>

      <section className="an-section">
        <div className="an-container">
          <Reveal>
          <h2 className="an-h2 an-h2--tight">Preguntas frecuentes.</h2>
          <div className="an-faq-list">
            {FAQS.map((f) => (
              <div key={f.q} className="an-faq-item">
                <h4 className="an-faq-q">{f.q}</h4>
                <p className="an-faq-a">{f.a}</p>
              </div>
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      <section className="an-cta">
        <div className="an-container an-cta-inner">
          <h2 className="an-h2">Deja de compartir tu perfil a ciegas.</h2>
          <p className="an-p an-cta-sub">Crea tu perfil gratis y mira tus primeras métricas hoy mismo.</p>
          <div className="an-cta-actions">
            <button onClick={() => navigate('/register')} className="an-btn-pill">
              Comenzar gratis <IconArrowRight/>
            </button>
            <button onClick={() => navigate('/cotizar')} className="an-btn-outline">
              Hablar con ventas
            </button>
          </div>
        </div>
      </section>

      <footer className="an-footer">
        <div className="an-container">
          <div className="an-related-row">
            <RelatedPagesLinks currentPath="/analytics" linkClassName="an-related-link" />
          </div>
          <div className="an-footer-inner">
            <ContactLogo/>
            <span className="an-footer-copy">© 2026 CONTACT Network SPA. Chile</span>
          </div>
        </div>
      </footer>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <style>{CSS}</style>
      <style>{REVEAL_CSS}</style>
    </div>
  );
};

const CSS = `
:root{
  --an-bg:#F9F9F8;
  --an-bg-w:#FFFFFF;
  --an-text:#000000;
  --an-muted:#4a4a4a;
  --an-faint:#C0C0C0;
  --an-border:#EEEEEC;
  --an-border-dk:#CCCCCA;
}

.an-root{font-family:'abcNormal','Inter','Arial',sans-serif;background:var(--an-bg);color:var(--an-text);min-height:100vh;overflow-x:hidden;line-height:1.6;position:relative}
.nx-hero-dither{position:fixed;inset:0;z-index:0;overflow:hidden;pointer-events:none;opacity:.27;-webkit-mask-image:linear-gradient(180deg,#000 0%,#000 40%,transparent 85%);mask-image:linear-gradient(180deg,#000 0%,#000 40%,transparent 85%);transform:translateZ(0);will-change:transform}

.an-nav{position:sticky;top:0;z-index:100;background:transparent}
.an-nav-inner{max-width:900px;margin:0 auto;padding:0 40px;height:64px;display:flex;align-items:center;justify-content:space-between}
.an-btn-back{display:inline-flex;align-items:center;gap:7px;background:none;border:none;color:var(--an-muted);font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:500;cursor:pointer;padding:0;transition:color .15s}
.an-btn-back:hover{color:var(--an-text)}
.an-nav-actions{display:flex;align-items:center;gap:10px}
.an-btn-acceder{display:inline-flex;align-items:center;background:var(--an-text);color:#fff;border:none;border-radius:100px;height:36px;padding:0 18px;font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:opacity .15s}
.an-btn-acceder:hover{opacity:.82}
.an-btn-comprar-nav{display:inline-flex;align-items:center;background:transparent;color:var(--an-text);border:1.5px solid var(--an-border-dk);border-radius:100px;height:36px;padding:0 18px;font-family:'Inter',system-ui,sans-serif;font-size:13px;font-weight:600;cursor:pointer;transition:all .15s}
.an-btn-comprar-nav:hover{border-color:var(--an-text)}

.an-container{max-width:900px;margin:0 auto;padding:0 40px}

.an-hero{padding:72px 0 56px}
.an-eyebrow{font-family:'Inter',system-ui,sans-serif;font-size:11px;font-weight:700;color:var(--an-muted);text-transform:uppercase;letter-spacing:.08em;margin-bottom:20px}
.an-h1{font-weight:300;font-size:clamp(32px,4.6vw,52px);line-height:1.15;letter-spacing:-.025em;color:var(--an-text);margin-bottom:26px;max-width:720px}
.an-lead{font-family:'Inter',system-ui,sans-serif;font-size:clamp(15px,1.6vw,17px);color:var(--an-muted);line-height:1.75;max-width:600px}

.an-section{padding:0 0 80px}
.an-h2{font-weight:300;font-size:clamp(22px,2.6vw,30px);line-height:1.3;letter-spacing:-.02em;color:var(--an-text);margin-bottom:32px;max-width:620px}
.an-h2--tight{margin-bottom:28px}
.an-p{font-family:'Inter',system-ui,sans-serif;font-size:15px;color:var(--an-muted);line-height:1.75;max-width:560px}

.an-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:1px;background:var(--an-border);border:1px solid var(--an-border)}
.an-card{background:var(--an-bg-w);padding:28px}
.an-card-icon{display:inline-flex;color:var(--an-text);margin-bottom:14px}
.an-card-title{font-family:'Inter',system-ui,sans-serif;font-size:15px;font-weight:700;color:var(--an-text);margin-bottom:8px;letter-spacing:-.01em}
.an-card-desc{font-family:'Inter',system-ui,sans-serif;font-size:13.5px;color:var(--an-muted);line-height:1.65}

.an-chart-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}
.an-chart-item{border-top:2px solid var(--an-text);padding-top:16px}
.an-chart-title{font-family:'Inter',system-ui,sans-serif;font-size:14.5px;font-weight:700;color:var(--an-text);margin-bottom:8px}
.an-chart-desc{font-family:'Inter',system-ui,sans-serif;font-size:13px;color:var(--an-muted);line-height:1.6}

.an-team-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:20px}
.an-team-card{background:var(--an-bg-w);border:1px solid var(--an-border);border-radius:14px;padding:24px}
.an-team-title{font-family:'Inter',system-ui,sans-serif;font-size:15px;font-weight:700;color:var(--an-text);margin-bottom:8px}
.an-team-desc{font-family:'Inter',system-ui,sans-serif;font-size:13.5px;color:var(--an-muted);line-height:1.65}

.an-plan-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px}
.an-plan-card{background:var(--an-bg-w);border:1px solid var(--an-border);border-radius:14px;padding:24px}
.an-plan-card--highlight{border-color:var(--an-text)}
.an-plan-name{font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:700;color:var(--an-text);margin-bottom:8px}
.an-plan-desc{font-family:'Inter',system-ui,sans-serif;font-size:13px;color:var(--an-muted);line-height:1.6}

.an-faq-list{display:flex;flex-direction:column;gap:22px;max-width:640px}
.an-faq-item{border-top:1px solid var(--an-border);padding-top:16px}
.an-faq-q{font-family:'Inter',system-ui,sans-serif;font-size:14.5px;font-weight:700;color:var(--an-text);margin-bottom:8px}
.an-faq-a{font-family:'Inter',system-ui,sans-serif;font-size:13.5px;color:var(--an-muted);line-height:1.65}

.an-cta{padding:0 0 80px}
.an-cta-inner{background:var(--an-bg-w);border:1px solid var(--an-border);border-radius:24px;padding:60px 48px;text-align:center;display:flex;flex-direction:column;align-items:center}
.an-cta-sub{text-align:center;margin:0 auto 32px}
.an-cta-actions{display:flex;align-items:center;gap:14px}
.an-btn-pill{display:inline-flex;align-items:center;gap:8px;background:var(--an-text);color:#fff;border:none;border-radius:100px;height:48px;padding:0 24px;font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:600;cursor:pointer;transition:opacity .15s,transform .2s cubic-bezier(.22,1,.36,1);white-space:nowrap}
.an-btn-pill:hover{opacity:.82;transform:translateY(-1px)}
.an-btn-outline{display:inline-flex;align-items:center;gap:8px;background:transparent;color:var(--an-text);border:1.5px solid var(--an-border-dk);border-radius:100px;height:48px;padding:0 24px;font-family:'Inter',system-ui,sans-serif;font-size:14px;font-weight:600;cursor:pointer;transition:all .15s;white-space:nowrap}
.an-btn-outline:hover{border-color:var(--an-text);background:var(--an-bg)}

.an-related-row{display:flex;flex-wrap:wrap;gap:10px;padding:28px 0;border-top:1px solid var(--an-border)}
.an-related-link{background:none;border:1px solid var(--an-border-dk);border-radius:100px;padding:7px 15px;color:var(--an-muted);font-family:'Inter',system-ui,sans-serif;font-size:12.5px;font-weight:600;cursor:pointer;transition:all .15s}
.an-related-link:hover{color:var(--an-text);border-color:var(--an-text)}

.an-footer-inner{display:flex;align-items:center;justify-content:space-between;padding:24px 0}
.an-footer-copy{font-family:'Inter',system-ui,sans-serif;font-size:12px;color:var(--an-faint)}

@media(max-width:640px){
  .an-nav-inner,.an-container{padding:0 20px}
  .an-nav-actions{gap:6px}
  .an-btn-acceder,.an-btn-comprar-nav{height:32px;padding:0 12px;font-size:12px}
  .an-btn-back{font-size:0}
  .an-hero{padding:48px 0 40px}
  .an-section,.an-cta{padding-bottom:56px}
  .an-grid,.an-chart-grid,.an-team-grid,.an-plan-grid{grid-template-columns:1fr}
  .an-cta-inner{padding:44px 22px}
  .an-cta-actions{flex-direction:column;width:100%}
  .an-cta-actions .an-btn-pill,.an-cta-actions .an-btn-outline{width:100%;justify-content:center}
  .an-footer-inner{flex-direction:column;gap:8px;text-align:center}
}
@media(prefers-reduced-motion:reduce){
  *{animation-duration:.01ms !important;transition-duration:.01ms !important}
}
`;

export default AnalyticsPage;
